// SPDX-License-Identifier: BUSL-1.1

/**
 * Feature flags management page.
 *
 */

import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { showSuccess, showApiError } from '@/lib/toast';
import { DataTable } from '@/components/data-table';
import { CreateFlagDialog } from '@/components/create-flag-dialog';
import { FlagFilters, type FlagFilterState } from '@/components/flag-filters';
import { KillSwitchDialog } from '@/components/kill-switch-dialog';
import { FlagListSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Flag } from '@/types/api';
import {
  PROVIDER_COLORS,
  CONFIG_TYPE_LABELS,
  LIFECYCLE_COLORS,
} from '@/types/api';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function providerBadge(provider?: string) {
  const p = provider ?? 'internal';
  const cls = PROVIDER_COLORS[p] ?? PROVIDER_COLORS.internal;
  return <Badge className={cls}>{p}</Badge>;
}

function lifecycleBadge(stage?: string) {
  if (!stage) return <span className="text-neutral-500">-</span>;
  const cls = LIFECYCLE_COLORS[stage] ?? 'bg-neutral-700 text-neutral-300';
  return <Badge className={cls}>{stage}</Badge>;
}

function applyFilters(flags: Flag[], filters: FlagFilterState): Flag[] {
  let result = flags;
  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (f) =>
        f.key.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q),
    );
  }
  if (filters.provider !== 'all') {
    result = result.filter(
      (f) => (f.provider ?? 'internal') === filters.provider,
    );
  }
  if (filters.lifecycle !== 'all') {
    result = result.filter((f) => f.lifecycleStage === filters.lifecycle);
  }
  return result;
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export function FlagsPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState<FlagFilterState>({
    search: '',
    provider: 'all',
    lifecycle: 'all',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['flags'],
    queryFn: () => apiFetch<Flag[]>('/flags'),
  });

  const toggle = useMutation({
    mutationFn: (flag: Flag) =>
      apiFetch(`/flags/${flag.key}/toggle`, {
        method: 'POST',
        body: JSON.stringify({ enabled: !flag.enabled }),
      }),
    onSuccess: (_data, flag) => {
      showSuccess(`Flag "${flag.key}" ${flag.enabled ? 'disabled' : 'enabled'}`);
      qc.invalidateQueries({ queryKey: ['flags'] });
    },
    onError: (err) => showApiError(err),
  });

  const killSwitch = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      apiFetch(`/flags/${key}/kill-switch`, {
        method: 'POST',
        body: JSON.stringify({
          enabled,
          reason: !enabled ? 'Manual kill switch' : 'Manual reactivation',
        }),
      }),
    onSuccess: (_data, { key, enabled }) => {
      showSuccess(`Kill switch ${!enabled ? 'activated' : 'deactivated'} for "${key}"`);
      qc.invalidateQueries({ queryKey: ['flags'] });
    },
    onError: (err) => showApiError(err),
  });

  const sync = useMutation({
    mutationFn: () => apiFetch('/flags/sync', { method: 'POST' }),
    onSuccess: () => {
      showSuccess('Provider sync complete');
      qc.invalidateQueries({ queryKey: ['flags'] });
    },
    onError: (err) => showApiError(err),
  });

  const columns = useMemo<ColumnDef<Flag>[]>(() => [
    {
      accessorKey: 'key',
      header: 'Flag Key',
      cell: ({ row }) => (
        <span className="font-mono text-blue-400">{row.original.key}</span>
      ),
    },
    {
      accessorKey: 'provider',
      header: 'Provider',
      cell: ({ row }) => providerBadge(row.original.provider),
    },
    {
      accessorKey: 'configType',
      header: 'Type',
      cell: ({ row }) => {
        const t = row.original.configType;
        return t
          ? <Badge variant="outline">{CONFIG_TYPE_LABELS[t] ?? t}</Badge>
          : <span className="text-neutral-500">-</span>;
      },
    },
    {
      accessorKey: 'lifecycleStage',
      header: 'Lifecycle',
      cell: ({ row }) => lifecycleBadge(row.original.lifecycleStage),
    },
    {
      accessorKey: 'enabled',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.enabled ? 'default' : 'destructive'}>
          {row.original.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: 'Last Updated',
      cell: ({ row }) => (
        <span className="text-neutral-400">
          {new Date(row.original.updatedAt).toLocaleString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const f = row.original;
        const togglePending = toggle.isPending && toggle.variables?.key === f.key;
        const killPending = killSwitch.isPending && killSwitch.variables?.key === f.key;
        return (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="gap-1.5"
              disabled={togglePending}
              onClick={() => toggle.mutate(f)}
            >
              {f.enabled ? <ToggleRight className="size-3.5" /> : <ToggleLeft className="size-3.5" />}
              {togglePending ? '...' : f.enabled ? 'Disable' : 'Enable'}
            </Button>
            <KillSwitchDialog
              flagKey={f.key}
              enabled={f.enabled}
              isPending={killPending}
              onConfirm={() => killSwitch.mutate({ key: f.key, enabled: !f.enabled })}
            />
          </div>
        );
      },
    },
  ], [toggle, killSwitch]);

  const filtered = data ? applyFilters(data, filters) : [];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-neutral-100">Feature Flags</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={sync.isPending}
            onClick={() => sync.mutate()}
          >
            <RefreshCw className={`size-3.5 ${sync.isPending ? 'animate-spin' : ''}`} />
            {sync.isPending ? 'Syncing...' : 'Sync'}
          </Button>
          <CreateFlagDialog />
        </div>
      </div>

      <FlagFilters filters={filters} onFiltersChange={setFilters} />

      {isLoading && <FlagListSkeleton />}

      {data && (
        <DataTable
          columns={columns}
          data={filtered}
          emptyMessage="No feature flags match the current filters."
        />
      )}
    </div>
  );
}
